import { type ComponentProps, useState } from "react";

import { AskMaraIcon } from "../ask-mara-icon/AskMaraIcon";
import { InsightDialog } from "../insight-dialog/InsightDialog";

export interface TableInsight {
  /** Markdown insights displayed in the dialog */
  insights: string[];
  isLoading?: boolean;
  /** When true, the dialog shows its error state instead of the insights */
  error?: boolean;
}

export interface TableInsightActionProps
  extends TableInsight,
    Omit<ComponentProps<typeof AskMaraIcon>, "onClick"> {
  /** Accessible dialog title, usually the title of the table the insight belongs to */
  title: string;
}

/**
 * Ask Mara icon that opens a dialog with the insights of a table.
 * While the insights are loading the icon is disabled; once ready it opens the
 * dialog, which shows its error state when the insights failed to load and its
 * empty state when there is no insight text.
 */
export function TableInsightAction({
  title,
  insights,
  isLoading = false,
  error = false,
  ...props
}: TableInsightActionProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  if (isLoading) {
    return <AskMaraIcon {...props} disabled />;
  }

  const content = insights
    .filter((insight) => insight.trim().length > 0)
    .join("\n\n");

  return (
    <>
      <AskMaraIcon
        isSelected={isOpen}
        onClick={() => setIsOpen(true)}
        {...props}
      />
      <InsightDialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        srOnlyTitle={title}
        isEmpty={content.length === 0}
        error={error}
        content={content}
      />
    </>
  );
}
